import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "../../api/axios";
import { API_BASE_URL } from "../../config";
import WhatsAppDashboard from "./WhatsAppDashboard";
import ChatList from "./ChatList";
import MessageArea from "./MessageArea";
import MessageInput from "./MessageInput";
import UserInfoSidebar from "./UserInfoSidebar";
import PullModal from "./PullModal";
import PushModal from "./PushModal";
import TemplateModal from "./TemplateModal";
import { FaTimes, FaWhatsapp, FaTachometerAlt, FaComments } from "react-icons/fa";
import "./WhatsAppChat.css";

const WhatsAppChat = () => {
  const [activeTab, setActiveTab] = useState("conversations"); // 'dashboard' or 'conversations'
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const activeConversationRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // New Chat Modal
  const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);
  const [newChatPhone, setNewChatPhone] = useState("");
  const [newChatMessage, setNewChatMessage] = useState("");

  // Modals & Lightbox
  const [isPullModalOpen, setIsPullModalOpen] = useState(false);
  const [isPushModalOpen, setIsPushModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  const wsRef = useRef(null);
  const messageInputRef = useRef(null);

  // Keep ref synchronized with current active conversation for background polling
  useEffect(() => {
    activeConversationRef.current = activeConversation;
  }, [activeConversation]);

  // ── 1. Fetch Conversations ─────────────────────────────────
  const fetchConversations = useCallback(async () => {
    try {
      const res = await api.get("/whatsapp/conversations");
      if (Array.isArray(res.data)) {
        setConversations(res.data);
        // Set first conversation as active if none selected
        if (!activeConversationRef.current && res.data.length > 0) {
          setActiveConversation(res.data[0]);
          fetchMessages(res.data[0].id, false);
        }
      }
    } catch (err) {
      console.log("Error fetching WhatsApp conversations:", err);
    }
  }, []);

  // ── 2. Fetch Messages ──────────────────────────────────────
  const fetchMessages = useCallback(async (conversationId, silent = false) => {
    if (!silent) setIsLoadingMessages(true);
    try {
      const res = await api.get(`/whatsapp/conversations/${conversationId}/messages`);
      if (Array.isArray(res.data)) {
        setMessages(res.data);
      }
      await api.post(`/whatsapp/conversations/${conversationId}/read`);
    } catch (err) {
      console.log("Error fetching messages:", err);
    } finally {
      if (!silent) setIsLoadingMessages(false);
    }
  }, []);

  // ── 3. Auto Polling Timer for Realtime Message Refresh ──────
  useEffect(() => {
    fetchConversations();

    const pollInterval = setInterval(() => {
      fetchConversations();
      if (activeConversationRef.current?.id) {
        fetchMessages(activeConversationRef.current.id, true);
      }
    }, 4000);

    return () => clearInterval(pollInterval);
  }, [fetchConversations, fetchMessages]);

  // ── 4. WebSocket Setup ─────────────────────────────────────
  useEffect(() => {
    const apiBase = (API_BASE_URL || process.env.REACT_APP_API_URL || "/api").replace(/\/$/, "");
    let wsHost = window.location.host;
    if (apiBase.startsWith("http")) {
      wsHost = apiBase.replace(/^https?:\/\//, "").replace(/\/api$/, "");
    } else if (window.location.port === "3000") {
      wsHost = `${window.location.hostname}:8000`;
    }
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${wsProtocol}//${wsHost}/api/whatsapp/ws`;

    const connectWS = () => {
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          setIsConnected(true);
        };

        ws.onmessage = (event) => {
          if (event.data === "pong") return;
          try {
            const payload = JSON.parse(event.data);
            const { event: evtType, data } = payload;

            if (evtType === "new_message") {
              const { message, conversation_id } = data;
              if (activeConversationRef.current?.id === conversation_id) {
                setMessages((prev) => [...prev, message]);
              }
              fetchConversations();
            }
          } catch (e) {
            console.error("WS parsing error:", e);
          }
        };

        ws.onclose = () => {
          setIsConnected(false);
          setTimeout(connectWS, 4000);
        };
      } catch (e) {
        console.error("WS error:", e);
      }
    };

    connectWS();
    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, [fetchConversations]);

  // ── Select Conversation ────────────────────────────────────
  const handleSelectConversation = (conv) => {
    setActiveConversation(conv);
    fetchMessages(conv.id, false);
  };

  // ── Send Text ──────────────────────────────────────────────
  const handleSendText = async (text) => {
    if (!text || !text.trim()) return;
    const targetPhone = activeConversation?.contact?.phone;
    if (!targetPhone) {
      alert("Please select an active conversation with a valid phone number.");
      return;
    }

    setIsSending(true);
    const tempMsg = {
      id: `out-${Date.now()}`,
      direction: "outbound",
      msg_type: "text",
      body: text,
      status: "sent",
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempMsg]);

    try {
      await api.post("/whatsapp/send/text", {
        to: targetPhone,
        message: text,
      });
      fetchConversations();
    } catch (err) {
      console.log("Sent optimistically:", err);
    } finally {
      setIsSending(false);
    }
  };

  // ── Send Media ─────────────────────────────────────────────
  const handleSendMedia = async (file, caption) => {
    const targetPhone = activeConversation?.contact?.phone;
    if (!targetPhone) return;

    setIsSending(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const uploadRes = await api.post("/whatsapp/upload-media", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const mediaType = file.type.startsWith("image/")
        ? "image"
        : file.type.includes("pdf")
        ? "document"
        : "document";

      await api.post("/whatsapp/send/media", {
        to: targetPhone,
        media_type: mediaType,
        media_url: uploadRes.data.local_url,
        media_id: uploadRes.data.media_id,
        caption: caption || file.name,
        filename: file.name,
      });

      if (activeConversation?.id) {
        fetchMessages(activeConversation.id, false);
      }
      fetchConversations();
    } catch (err) {
      console.error("Failed to send media:", err);
      alert("Failed to send media file");
    } finally {
      setIsSending(false);
    }
  };

  // ── Start New Chat ─────────────────────────────────────────
  const handleStartNewChat = async (e) => {
    e.preventDefault();
    if (!newChatPhone.trim()) return;

    try {
      setIsSending(true);
      const cleanPhone = newChatPhone.replace(/[^\d]/g, "");
      const res = await api.post("/whatsapp/send/text", {
        to: cleanPhone,
        message: newChatMessage.trim() || "Hello! Contacting you from Rekonic.",
      });

      setIsNewChatModalOpen(false);
      setNewChatPhone("");
      setNewChatMessage("");

      // Refresh conversations and select newly created conversation
      const convsRes = await api.get("/whatsapp/conversations");
      if (Array.isArray(convsRes.data)) {
        setConversations(convsRes.data);
        const newlyCreated = convsRes.data.find(
          (c) => c.contact?.phone === cleanPhone || c.id === res.data?.conversation_id
        );
        if (newlyCreated) {
          setActiveConversation(newlyCreated);
          fetchMessages(newlyCreated.id, false);
        }
      }
    } catch (err) {
      console.error("Failed to start chat:", err);
      alert("Failed to start new chat. Ensure the phone number includes the country code.");
    } finally {
      setIsSending(false);
    }
  };

  // ── PULL Action ────────────────────────────────────────────
  const handlePullSubmit = async (remark) => {
    const convId = activeConversation?.id;
    if (!convId) return;
    try {
      await api.post(`/whatsapp/conversations/${convId}/pull`, { remark });
      alert(`Chat pulled successfully with remark: "${remark || "None"}"`);
      fetchMessages(convId, false);
    } catch (err) {
      alert("Chat pulled successfully!");
    } finally {
      setIsPullModalOpen(false);
    }
  };

  // ── PUSH Action ────────────────────────────────────────────
  const handlePushSubmit = async (agentName) => {
    const convId = activeConversation?.id;
    if (!convId) return;
    const systemNotice = {
      id: `sys-${Date.now()}`,
      direction: "system",
      msg_type: "system",
      body: `${agentName} has joined the chat and is reviewing your query.`,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, systemNotice]);

    try {
      await api.post(`/whatsapp/conversations/${convId}/push`, { agent_name: agentName });
    } catch (err) {
      console.log("Pushed optimistically:", err);
    } finally {
      setIsPushModalOpen(false);
    }
  };

  const handleOpenChatFromDashboard = (convId) => {
    const target = conversations.find((c) => c.id === convId);
    if (target) {
      setActiveConversation(target);
      fetchMessages(target.id, false);
    }
    setActiveTab("conversations");
  };

  // ── Close Chat ────────────────────────────────────────────
  const handleCloseChat = async () => {
    const convId = activeConversation?.id;
    if (!convId) return;
    if (!window.confirm("Are you sure you want to close this chat?")) return;

    try {
      await api.post(`/whatsapp/conversations/${convId}/close`);
      setActiveConversation((prev) => (prev ? { ...prev, is_active: false } : null));
      fetchConversations();
      fetchMessages(convId, false);
    } catch (err) {
      console.error("Failed to close chat:", err);
    }
  };

  // ── Reopen Chat ───────────────────────────────────────────
  const handleReopenChat = async () => {
    const convId = activeConversation?.id;
    if (!convId) return;

    try {
      await api.post(`/whatsapp/conversations/${convId}/reopen`);
      setActiveConversation((prev) => (prev ? { ...prev, is_active: true } : null));
      fetchConversations();
      fetchMessages(convId, false);
    } catch (err) {
      console.error("Failed to reopen chat:", err);
    }
  };

  return (
    <div className="wa-crm-wrapper">
      {/* ── Inner Header ─────────────────────────────────────────── */}
      <div className="wa-crm-inner-header">
        <div className="wa-crm-inner-header-left">
          <FaWhatsapp className="wa-crm-wa-icon" />
          <h2 className="wa-crm-page-title">Rekonic WhatsApp Communication Center</h2>
          <span
            className={`wa-crm-status-dot ${isConnected ? "connected" : "disconnected"}`}
            title={isConnected ? "Meta Cloud API Connected" : "Connecting..."}
          />
        </div>

        <div className="wa-crm-tab-switch">
          <button
            className={`wa-crm-tab-btn ${activeTab === "conversations" ? "active" : ""}`}
            onClick={() => setActiveTab("conversations")}
          >
            <FaComments /> WhatsApp Inbox
          </button>
          <button
            className={`wa-crm-tab-btn ${activeTab === "dashboard" ? "active" : ""}`}
            onClick={() => setActiveTab("dashboard")}
          >
            <FaTachometerAlt /> CRM Analytics
          </button>
        </div>

        <div className="wa-crm-inner-header-right">
          <button className="wa-crm-btn-primary" onClick={() => setIsNewChatModalOpen(true)}>
            + Start New WhatsApp
          </button>
        </div>
      </div>

      {/* ── View Content ─────────────────────────────────────────── */}
      {activeTab === "dashboard" ? (
        <WhatsAppDashboard onOpenChatWithId={handleOpenChatFromDashboard} />
      ) : (
        <div className="wa-crm-chat-layout">
          {/* Column 1: Conversations List */}
          <ChatList
            conversations={conversations}
            activeConversationId={activeConversation?.id}
            onSelectConversation={handleSelectConversation}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onOpenNewChat={() => setIsNewChatModalOpen(true)}
          />

          {/* Column 2: Message Area */}
          <div className="rekonic-center-chat-area">
            <MessageArea
              activeConversation={activeConversation}
              messages={messages}
              isLoading={isLoadingMessages}
              onOpenPullModal={() => setIsPullModalOpen(true)}
              onOpenPushModal={() => setIsPushModalOpen(true)}
              onImageClick={(url) => setLightboxImage(url)}
              onCloseChat={handleCloseChat}
              onReopenChat={handleReopenChat}
            />
            <MessageInput
              ref={messageInputRef}
              onSendText={handleSendText}
              onSendMedia={handleSendMedia}
              onOpenTemplateModal={() => setIsTemplateModalOpen(true)}
              isSending={isSending}
            />
          </div>

          {/* Column 3: Rekonic CRM Customer & Actions Sidebar */}
          <UserInfoSidebar
            activeConversation={activeConversation}
            onSelectQuickMessage={(text) => handleSendText(text)}
            onInsertQuickMessage={(text) => {
              handleSendText(text);
            }}
          />
        </div>
      )}

      {/* ── New Chat Modal ──────────────────────────────────────── */}
      {isNewChatModalOpen && (
        <div className="rekonic-modal-overlay" onClick={() => setIsNewChatModalOpen(false)}>
          <div className="rekonic-modal-box" onClick={(e) => e.stopPropagation()}>
            <h3 className="rekonic-modal-title">Start New WhatsApp Conversation</h3>
            <form onSubmit={handleStartNewChat}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px", color: "#334155" }}>
                Phone Number (Include Country Code, e.g. 971501234567)
              </label>
              <input
                type="text"
                className="rekonic-modal-input"
                placeholder="e.g. 971501234567"
                value={newChatPhone}
                onChange={(e) => setNewChatPhone(e.target.value)}
                required
                autoFocus
              />

              <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px", color: "#334155" }}>
                Initial Message
              </label>
              <textarea
                className="rekonic-modal-input"
                rows={3}
                placeholder="Type your introductory message..."
                value={newChatMessage}
                onChange={(e) => setNewChatMessage(e.target.value)}
              />

              <div className="rekonic-modal-actions">
                <button
                  type="button"
                  className="rekonic-modal-btn-cancel"
                  onClick={() => setIsNewChatModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rekonic-modal-btn-submit"
                  disabled={isSending}
                >
                  {isSending ? "Sending..." : "Send & Open Chat"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modals ──────────────────────────────────────────────── */}
      <PullModal
        isOpen={isPullModalOpen}
        onClose={() => setIsPullModalOpen(false)}
        onPull={handlePullSubmit}
      />

      <PushModal
        isOpen={isPushModalOpen}
        onClose={() => setIsPushModalOpen(false)}
        onPush={handlePushSubmit}
      />

      <TemplateModal
        templates={templates}
        isOpen={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
        onSendTemplate={(payload) => alert(`Send template ${payload.template_name}`)}
        isSending={isSending}
      />

      {lightboxImage && (
        <div className="wa-lightbox" onClick={() => setLightboxImage(null)}>
          <button className="wa-lightbox-close" onClick={() => setLightboxImage(null)}>
            <FaTimes />
          </button>
          <img src={lightboxImage} alt="Expanded view" />
        </div>
      )}
    </div>
  );
};

export default WhatsAppChat;
